import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextInput } from "react-native-paper";
import Box from "../Box";
import { CustomListSubheader } from "../CustomListSubheader";
import DateTimePickerModal from "../DateTimePickerModal";

type Props = {
	value: { from: string; to: string };
	onChange: (value: { from: string; to: string }) => void;
	minimumDate?: Date;
};

export const DateRangeInput = ({ value, onChange, minimumDate }: Props) => {
	const { t } = useTranslation();
	const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
	const [activeDateField, setActiveDateField] = useState<"from" | "to">("from");

	const activeDate = useMemo(() => {
		const activeValue = activeDateField === "from" ? value.from : value.to;
		return activeValue ? dayjs(activeValue, "MM/DD/YYYY").toDate() : undefined;
	}, [activeDateField, value.to, value.from]);

	const handleDateConfirm = (date: Date) => {
		const formattedDate = date.toLocaleDateString("en-US", {
			month: "numeric",
			day: "numeric",
			year: "numeric",
		});

		onChange({ ...value, [activeDateField]: formattedDate });
		setIsDatePickerVisible(false);
	};

	const openPickerFor = (field: "from" | "to") => {
		setActiveDateField(field);
		setIsDatePickerVisible(true);
	};

	return (
		<>
			<CustomListSubheader style={{ paddingBottom: 0 }}>
				{t("cme.selectDateRange")}
			</CustomListSubheader>
			<Box flexDirection="row" gap={16} paddingHorizontal={16}>
				<Box flex={1}>
					<TextInput
						mode="outlined"
						label={t("cme.from")}
						value={value.from}
						right={
							<TextInput.Icon
								icon="calendar"
								onPress={() => openPickerFor("from")}
							/>
						}
						onPressIn={() => openPickerFor("from")}
						showSoftInputOnFocus={false}
					/>
				</Box>
				<Box flex={1}>
					<TextInput
						mode="outlined"
						label={t("cme.to")}
						value={value.to}
						right={
							<TextInput.Icon
								icon="calendar"
								onPress={() => openPickerFor("to")}
							/>
						}
						onPressIn={() => openPickerFor("to")}
						showSoftInputOnFocus={false}
					/>
				</Box>
			</Box>

			<DateTimePickerModal
				mode="date"
				date={activeDate}
				minimumDate={minimumDate}
				isVisible={isDatePickerVisible}
				onCancel={() => setIsDatePickerVisible(false)}
				onConfirm={handleDateConfirm}
			/>
		</>
	);
};
